/**
 * Error handling utility for better error messages
 * Shows detailed error information to user instead of generic messages
 */

interface ApiErrorResponse {
  success?: boolean;
  message?: string;
  errors?: Record<string, string[]>;
  data?: any;
}

/**
 * Extract error message from API response or exception
 * Returns detailed message if available, otherwise generic message
 */
export const getErrorMessage = (error: any, defaultMessage: string = 'Gagal memproses permintaan'): string => {
  // If error has response (axios/API error)
  if (error?.response?.data) {
    const data: ApiErrorResponse = error.response.data;
    
    // If server provided a message, use it
    if (data.message) {
      return data.message;
    }
    
    // If there are validation errors, show first error
    if (data.errors && typeof data.errors === 'object') {
      const fieldErrors = Object.values(data.errors)[0];
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        return fieldErrors[0];
      }
    }
  }
  
  // If error has a message (built-in Error)
  if (error?.message && error.message !== 'Network Error') {
    return error.message;
  }
  
  return defaultMessage;
};

/**
 * Get all validation errors for display
 * Useful for showing multiple validation errors at once
 */
export const getValidationErrors = (error: any): Record<string, string> => {
  if (error?.response?.data?.errors && typeof error.response.data.errors === 'object') {
    const errors: Record<string, string> = {};
    for (const [field, messages] of Object.entries(error.response.data.errors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        errors[field] = messages[0];
      }
    }
    return errors;
  }
  return {};
};

/**
 * Log error for debugging
 * Only logs in development or when explicitly enabled
 */
export const logError = (context: string, error: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error:`, error);
    
    if (error?.response?.data) {
      console.error('Response Data:', error.response.data);
    }
  }
};

/**
 * Parse error from various sources and return standardized format
 */
export const parseError = (error: any, context: string = 'API') => {
  const message = getErrorMessage(error);
  const validationErrors = getValidationErrors(error);
  
  logError(context, error);
  
  return {
    message,
    validationErrors,
    statusCode: error?.response?.status,
    isValidationError: error?.response?.status === 422,
    isAuthError: error?.response?.status === 401 || error?.response?.status === 403,
    isNotFoundError: error?.response?.status === 404,
  };
};
